import React, { useState, useEffect, useCallback } from "react";
import {
  Button, ButtonGroup, Card, Col, Row, Modal, Badge,
  Form, InputGroup, Spinner, Pagination,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory, faEye, faCalendarAlt, faUser, faFileAlt,
  faMoneyBillWave, faSearch, faFilter, faSync,
  faCheckCircle, faClock, faExclamationTriangle, faTimesCircle,
  faPlay, faGavel, faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { getSector, postApi } from "../../../redux/api";
import { formatNumber } from "../../../redux/utils";
import CustomTypeahead from "../../components/CustomTypeahead";
import CustomTable from "../../../components/CustomTable";
import moment from "moment";
import { Routes } from "../../../routes";
import { useNavigate } from "react-router-dom";

const STATUS_BADGE = (status) => {
  const s = status?.toLowerCase() || "";
  if (s.includes("paid") || s.includes("approved") || s.includes("completed")) return "success";
  if (s.includes("return") || s.includes("reject")) return "danger";
  if (s.includes("pending") || s.includes("review") || s.includes("process")) return "warning";
  return "info";
};

const STATUS_OPTIONS = [
  "Budget Approval Released", "Approval Processed", "Return Processed",
  "Approval Approved", "Return Approved", "Approval Queried",
  "Payment Schedule", "Return Schedule", "AG Approved", "Paid",
];

function HistoryTimeline({ history }) {
  if (!history?.length) return (
    <div className="text-center p-4 text-muted">
      <FontAwesomeIcon icon={faHistory} size="2x" className="mb-2" />
      <p>No history recorded for this release.</p>
    </div>
  );
  return (
    <div style={{ position: "relative", paddingLeft: 40 }}>
      <div style={{
        position: "absolute", left: 18, top: 0, bottom: 0,
        width: 2, background: "#e9ecef",
      }} />
      {history.map((h, i) => (
        <div key={i} style={{ position: "relative", marginBottom: 20 }}>
          <div style={{
            position: "absolute", left: -29, top: 4,
            width: 20, height: 20, borderRadius: "50%",
            background: "#0d6efd", border: "3px solid #fff",
            boxShadow: "0 0 0 2px #0d6efd",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#fff", fontSize: 8 }} />
          </div>
          <Card className="border-0 shadow-sm" style={{ borderLeft: "3px solid #0d6efd !important" }}>
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>{h.status || h.approval_status}</strong>
                  {h.stage && <span className="text-muted ms-2 small">— {h.stage}</span>}
                </div>
                <small className="text-muted">{moment(h.updated_at || h.action_date).format("DD MMM YYYY, HH:mm")}</small>
              </div>
              {h.updated_by_name && (
                <div className="text-muted small mt-1">
                  <FontAwesomeIcon icon={faUser} className="me-1" />
                  {h.updated_by_name}
                </div>
              )}
              {h.remarks && (
                <div className="mt-1 p-2 bg-light rounded small">{h.remarks}</div>
              )}
            </Card.Body>
          </Card>
        </div>
      ))}
    </div>
  );
}

function AdminAllReleasesTracking() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mdaOptions, setMdaOptions] = useState([]);

  // filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [mdaFilter, setMdaFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(moment().subtract(1, "month").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));

  // pagination
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, limit: 50 });

  const fetchData = useCallback(() => {
    setLoading(true);
    postApi("post_approval_collection", {
      form: {
        query_type: "all_releases_admin",
        page,
        limit: 50,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(mdaFilter && { mda_name: mdaFilter }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      },
    })
      .then((data) => {
        if (data?.result) {
          setList(data.result);
          setPagination(data.pagination || { total: data.result.length, totalPages: 1, limit: 50 });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, mdaFilter, dateFrom, dateTo]);

  useEffect(() => {
    getSector({ type: "mdas" }, "mdas", (d) => {
      setMdaOptions((d || []).map((i) => ({ mda_name: i.description, mda_code: i.head })));
    });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = () => { setPage(1); fetchData(); };
  const handleClear = () => {
    setSearch(""); setStatusFilter(""); setMdaFilter("");
    setDateFrom(moment().subtract(1, "month").format("YYYY-MM-DD"));
    setDateTo(moment().format("YYYY-MM-DD"));
    setPage(1);
  };

  const fields = [
    { title: "Approval No.", value: "approval_no" },
    { title: "MDA Name", value: "mda_name" },
    { title: "MDA Code", value: "mda_code" },
    {
      title: "Amount (₦)", custom: true,
      component: (item) => <div className="text-end">{formatNumber(item.amount)}</div>,
    },
    { title: "Approval Type", value: "approval_type" },
    {
      title: "Status", custom: true,
      component: (item) => (
        <Badge bg={STATUS_BADGE(item.approval_status || item.current_status)}>
          {item.approval_status || item.current_status}
        </Badge>
      ),
    },
    {
      title: "Date", custom: true,
      component: (item) => <div>{moment(item.approval_date || item.collection_date).format("DD/MM/YYYY")}</div>,
    },
    {
      title: "Actions", custom: true,
      component: (item) => (
        <div className="d-flex gap-1">
          <Button size="sm" variant="outline-primary" onClick={() => { setSelected(item); setShowModal(true); }}>
            <FontAwesomeIcon icon={faHistory} className="me-1" />History
          </Button>
          <Button
            size="sm" variant="outline-secondary"
            onClick={() => navigate(`${Routes.approval.viewApproval}?approval_no=${encodeURIComponent(item.approval_no)}&from=all-releases-admin`)}
          >
            <FontAwesomeIcon icon={faEye} className="me-1" />View
          </Button>
        </div>
      ),
    },
  ];

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;
    const pages = [];
    for (let p = 1; p <= Math.min(pagination.totalPages, 10); p++) {
      pages.push(
        <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-between align-items-center p-3">
        <small className="text-muted">
          Showing {((page - 1) * pagination.limit) + 1}–{Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
        </small>
        <Pagination className="mb-0">
          <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
          {pages}
          <Pagination.Next disabled={page === pagination.totalPages} onClick={() => setPage(p => p + 1)} />
        </Pagination>
      </div>
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center py-4">
        <div>
          <h4><FontAwesomeIcon icon={faFileAlt} className="me-2" />All Releases Tracking</h4>
          <p className="mb-0 text-muted">Admin view — all releases across all MDAs. Total: <strong>{pagination.total}</strong></p>
        </div>
        <ButtonGroup>
          <Button variant="outline-primary" size="sm" onClick={fetchData}>
            <FontAwesomeIcon icon={faSync} className="me-1" />Refresh
          </Button>
        </ButtonGroup>
      </div>

      {/* Filters */}
      <Card border="light" className="shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={3}>
              <Form.Label className="small">Search</Form.Label>
              <InputGroup size="sm">
                <Form.Control
                  placeholder="Approval No, MDA..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                <Button variant="outline-primary" onClick={handleSearch}>
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Label className="small">Status</Form.Label>
              <Form.Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">MDA Name</Form.Label>
              <CustomTypeahead
                labelKey="mda_name"
                options={mdaOptions}
                selected={mdaFilter ? [{ mda_name: mdaFilter }] : []}
                onChange={(s) => setMdaFilter(s.length ? s[0].mda_name : "")}
                placeholder="Select MDA..."
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Date From</Form.Label>
              <Form.Control size="sm" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Date To</Form.Label>
              <Form.Control size="sm" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button size="sm" variant="outline-secondary" className="w-100" onClick={handleClear}>
                <FontAwesomeIcon icon={faFilter} /> Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="p-0" style={{ minHeight: 400 }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
              <Spinner animation="border" />
            </div>
          ) : list.length > 0 ? (
            <div style={{ height: "65vh", overflow: "auto" }}>
              <CustomTable fields={fields} data={list} />
            </div>
          ) : (
            <div className="text-center p-5 text-muted">
              <FontAwesomeIcon icon={faFileAlt} size="3x" className="mb-3" />
              <h5>No releases found</h5>
              <p>Try adjusting your filters.</p>
            </div>
          )}
        </Card.Body>
        {renderPagination()}
      </Card>

      {/* History Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHistory} className="me-2 text-primary" />
            Release History — <strong>{selected?.approval_no}</strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <>
              <Card className="mb-3 border-0 bg-light">
                <Card.Body className="py-2">
                  <Row>
                    <Col md={4}><small className="text-muted d-block">MDA</small><strong>{selected.mda_name}</strong></Col>
                    <Col md={4}><small className="text-muted d-block">Amount</small><strong>₦{formatNumber(selected.amount)}</strong></Col>
                    <Col md={4}>
                      <small className="text-muted d-block">Status</small>
                      <Badge bg={STATUS_BADGE(selected.approval_status || selected.current_status)}>
                        {selected.approval_status || selected.current_status}
                      </Badge>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              <HistoryTimeline history={selected.status_history} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Close</Button>
          {selected && (
            <Button
              variant="primary"
              onClick={() => {
                setShowModal(false);
                navigate(`${Routes.approval.viewApproval}?approval_no=${encodeURIComponent(selected.approval_no)}&from=all-releases-admin`);
              }}
            >
              <FontAwesomeIcon icon={faEye} className="me-1" />View Full Release
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AdminAllReleasesTracking;
